import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Mail, RefreshCw, LogOut, CheckCircle } from 'lucide-react';

const VerifyEmail = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [seconds, setSeconds] = useState(60);
  const [resendDisabled, setResendDisabled] = useState(true);
  const { currentUser, verifyEmail, logout } = useAuth();
  const navigate = useNavigate();

  // Redirect if user is not logged in or already verified
  useEffect(() => {
    if (!currentUser) {
      navigate('/signin');
    } else if (currentUser.emailVerified) {
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);

  // Countdown timer for resend button
  useEffect(() => {
    if (seconds > 0 && resendDisabled) {
      const timerId = setTimeout(() => setSeconds(seconds - 1), 1000);
      return () => clearTimeout(timerId);
    } else if (seconds === 0 && resendDisabled) {
      setResendDisabled(false);
    }
  }, [seconds, resendDisabled]);

  // Handle resend verification email
  const handleResendEmail = async () => {
    try {
      setLoading(true);
      await verifyEmail();
      setMessage('Verification email sent! Please check your inbox.');
      setResendDisabled(true);
      setSeconds(60);
    } catch (err) {
      console.error('Error sending verification email:', err);
      setMessage('Failed to send verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/signin');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  // Handle manual verification check
  const handleCheckVerification = async () => {
    try {
      setLoading(true);
      // Reload the user to check if email has been verified
      await currentUser.reload();
      if (currentUser.emailVerified) {
        navigate('/dashboard');
      } else {
        setMessage('Email not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (err) {
      console.error('Error checking verification status:', err);
      setMessage('Failed to check verification status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-indigo-100 mb-4">
            <Mail className="h-8 w-8 text-indigo-600" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Verify Your Email</h1>
          <p className="mt-2 text-sm text-gray-600">
            We've sent a verification link to <span className="font-medium">{currentUser.email}</span>
          </p>
        </div>

        {message && (
          <div className="rounded-md bg-blue-50 p-4 mb-4">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-blue-400" aria-hidden="true" />
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-800">{message}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Next steps:</h3>
          <ol className="list-decimal pl-5 text-sm text-gray-600 space-y-2">
            <li>Check your email inbox (and spam folder)</li>
            <li>Click the verification link in the email</li>
            <li>Return to this page and continue</li>
          </ol>
        </div>

        <div className="flex flex-col space-y-3">
          <button
            onClick={handleCheckVerification}
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-70 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <CheckCircle className="mr-2 h-5 w-5" aria-hidden="true" />
            I've Verified My Email
          </button>

          <button
            onClick={handleResendEmail}
            disabled={loading || resendDisabled}
            className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-70 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <RefreshCw className="mr-2 h-5 w-5" aria-hidden="true" />
            {resendDisabled ? `Resend Email (${seconds}s)` : 'Resend Verification Email'}
          </button>

          <button
            onClick={handleSignOut}
            className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <LogOut className="mr-2 h-5 w-5" aria-hidden="true" />
            Sign Out
          </button>
        </div>

        <div className="text-center text-sm text-gray-500 mt-4">
          <p>
            Need help?{' '}
            <Link to="/contact" className="font-medium text-indigo-600 hover:text-indigo-500">
              Contact Support
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;